// Motion primitives ported from the Vienna motion films (motion/01-email-agent, motion/02-one-place) so the
// lander and product feature cards move the way the films do: human-cadence typing, documents set down as
// whole pages with cites lighting in sequence, springy entrances, and click ripples.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import './FilmMotion.css';

export const FILM_SPRING = { type: 'spring', stiffness: 170, damping: 20, mass: 0.9 };
export const FILM_EASE = [0.22, 1, 0.36, 1];

// Deterministic jitter so a given string always types with the same rhythm.
const jitter = (i) => {
    const x = Math.sin(i * 12.9898 + 78.233) * 43758.5453;
    return x - Math.floor(x);
};

// Per-glyph start offsets: jitter on every key, a pause after punctuation, a beat at spaces (the film's typed()).
// Normalised to `duration` so callers keep their storyboard timing.
export function typingSchedule(text, duration) {
    const times = [];
    let at = 0;
    [...text].forEach((ch, i) => {
        times.push(at);
        at += 1 + (jitter(i) - 0.5) * 0.5 + (/[.,:;?!]/.test(ch) ? 3.3 : 0) + (ch === ' ' ? 0.45 : 0);
    });
    const scale = at > 0 ? duration / at : 0;
    return { times: times.map(t => t * scale), end: duration };
}

// Drop-in for the cards' linear typewriters: returns the visible prefix of `text` while `active`.
export function useFilmTyping(text, active, duration) {
    const reduce = useReducedMotion();
    const [count, setCount] = useState(0);
    useEffect(() => {
        if (!active || !text) {
            setCount(0);
            return undefined;
        }
        if (reduce) {
            setCount(text.length);
            return undefined;
        }
        const { times } = typingSchedule(text, duration);
        const ids = times.map((t, i) => setTimeout(() => setCount(i + 1), t));
        setCount(0);
        return () => ids.forEach(clearTimeout);
    }, [text, active, duration, reduce]);
    return active ? text.slice(0, count) : '';
}

// Citations render as linked cites (film 1's "cites linked" beat); dates and "Missing:" flags get a highlight.
const NAME = String.raw`[A-Z][A-Za-z.'’&-]*(?:,? (?:[A-Z][A-Za-z.'’&-]*|of|the|for|and)){0,5}`;
const REPORTER = String.raw`(?:U\.S\.|S\. Ct\.|L\. Ed\. 2d|F\.(?: Supp\.)?(?: [234]d| 4th)?|F\.[234]d)`;
const CITE = [
    String.raw`\b${NAME} v\. ${NAME}, \d+ ${REPORTER} \d+(?:, \d+)?(?: \([^)]*\d{4}\))?`,
    String.raw`\b\d+ U\.S\.C\. § ?\d+(?:\([A-Za-z0-9]+\))*`,
    String.raw`\bRule \d+\([a-z]\), Rules Governing Section \d+ Cases`,
    String.raw`\b(?:Fed\. R\. (?:Civ|Crim|App)\. P\.|(?:[A-Z][a-z]*\. )*L\.R\.|Dkt\.)\s*[\d.()a-z]*[\d)a-z]`,
].join('|');
const NOTE = String.raw`\b(?:January|February|March|April|May|June|July|August|September|October|November|December) \d{1,2}(?:, \d{4})?|\bMissing:`;
const DOC_MARK_RE = new RegExp(`(${CITE})|(${NOTE})`, 'g');

const isCaps = (line) => /[A-Z]/.test(line) && line === line.toUpperCase();
const isLead = (line) => line.length < 90 && (/^\d+\.\s/.test(line) || /:$/.test(line));

function parseBlock(lines, i) {
    if (lines.length === 1 && /^-{5,}$/.test(lines[0])) return { type: 'rule' };
    if (i === 0) return { type: 'title', text: lines.join(' ') };
    if (lines.length === 1 && (isCaps(lines[0]) || isLead(lines[0]))) return { type: 'heading', text: lines[0] };
    const lead = lines.length > 1 && isLead(lines[0]) ? lines[0] : null;
    const rest = lead ? lines.slice(1) : lines;
    const prose = rest.some(l => l.length > 50);
    return { type: prose ? 'para' : 'lines', lead, text: prose ? rest.join(' ') : rest.join('\n') };
}

// Plain draft text -> pleading blocks: caps lines are headings, a numbered or colon-ended first line leads its
// paragraph, hard-wrapped prose is rejoined, short-line groups (signature blocks) keep their breaks.
// Everything above the first paragraph is the caption and is centered.
export function parseDocument(text) {
    let inBody = false;
    return text.split(/\n\s*\n/).map(g => g.split('\n').map(l => l.trim()).filter(Boolean)).filter(g => g.length).map((lines, i) => {
        const block = parseBlock(lines, i);
        if (block.type === 'para') inBody = true;
        return { ...block, caption: !inBody && block.type !== 'rule' };
    });
}

export function withMarks(text) {
    const out = [];
    let last = 0;
    for (const m of text.matchAll(DOC_MARK_RE)) {
        if (m.index > last) out.push(text.slice(last, m.index));
        out.push(m[1]
            ? <a key={m.index} className="film-cite" href="#cite" tabIndex={-1} onClick={(e) => e.preventDefault()}>{m[1]}</a>
            : <mark key={m.index} className="film-mark">{m[2]}</mark>);
        last = m.index + m[0].length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}

const PAGE_W = 600;
const PAGE_GUTTER = 14;
// How much of the page (in page px) should be on screen. The films show most of the first page so it
// reads as a document, not a header, so short frames shrink the sheet instead of cropping to the caption.
const PAGE_VISIBLE = 470;
const CHIP_STRIP = 38;      // room above the sheet for the citations counter
const LAND_MS = 1000;       // sheet springs in and settles before the camera moves
const READ_PX_PER_S = 90;   // reading pace, in page px per second
const MIN_GLIDE_MS = 2200;
const END_HOLD_MS = 1800;
const END_GAP = 34;         // screen px of desk left under the page's bottom edge at the end of the read
const LIFT_MS = 520;

// Text passing up under the counter strip fades out instead of being sliced at the frame edge.
const viewportMask = (top) => `linear-gradient(to bottom, transparent 0, #000 ${top}px, #000 calc(100% - 22px), transparent 100%)`;

const easeInOutSine = (k) => -(Math.cos(Math.PI * k) - 1) / 2;
// Overshoots a touch and settles: the films' spring, as a curve the frame loop can sample.
const easeOutBack = (k) => 1 + 2.4 * Math.pow(k - 1, 3) + 1.4 * Math.pow(k - 1, 2);
const ENTER_MS = 950;

// Film 1's document beat, as a loop: the sheet lands, the camera reads down the whole page at a steady pace
// until the page's bottom edge (folio and all) sits on the desk, each cite turns into a lit link as it
// reaches the reading line and the counter ticks, the camera holds on the end, then the sheet lifts away
// and lands again from the top (never a scroll-back). Typography is
// laid out at a fixed page width and scaled as a unit, so the page keeps pleading proportions at any size.
// Pauses off screen; static with every cite lit under reduced motion. `desk` adds the films' grid backdrop.
export function DocumentPage({ text, className = '', desk = false }) {
    const reduce = useReducedMotion();
    const frameRef = useRef(null);
    const sheetRef = useRef(null);
    const cameraRef = useRef(null);
    const pageRef = useRef(null);
    const countRef = useRef(null);
    const [scale, setScale] = useState(0);
    const blocks = useMemo(() => parseDocument(text), [text]);
    const citeTotal = useMemo(() => [...text.matchAll(DOC_MARK_RE)].filter(m => m[1]).length, [text]);

    const top = citeTotal ? CHIP_STRIP : PAGE_GUTTER;

    useLayoutEffect(() => {
        const el = frameRef.current;
        if (!el) return undefined;
        const fit = () => {
            const w = el.clientWidth, h = el.clientHeight;
            if (!w) return;
            const byWidth = (w - PAGE_GUTTER * 2) / PAGE_W;
            const byHeight = h ? (h - top) / PAGE_VISIBLE : byWidth;
            setScale(Math.min(1, byWidth, byHeight));
        };
        fit();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(fit);
        ro.observe(el);
        return () => ro.disconnect();
    }, [top]);

    useEffect(() => {
        const frame = frameRef.current, sheet = sheetRef.current, camera = cameraRef.current, page = pageRef.current;
        if (!frame || !sheet || !camera || !page || !scale) return undefined;
        const marks = [...page.querySelectorAll('.film-cite, .film-mark')];
        const setCount = (n) => { if (countRef.current) countRef.current.textContent = `${n}/${citeTotal}`; };
        if (reduce) {
            marks.forEach(m => m.classList.add('is-lit'));
            setCount(citeTotal);
            return undefined;
        }
        marks.forEach(m => m.classList.remove('is-lit'));
        setCount(0);
        const visible = (frame.clientHeight - top) / scale;
        // Travel until the sheet's bottom edge sits END_GAP screen px above the frame's bottom.
        const travel = Math.max(0, sheet.offsetHeight - (frame.clientHeight - top - END_GAP) / scale);
        const glide = Math.max(MIN_GLIDE_MS, (travel / READ_PX_PER_S) * 1000);
        let onScreen = true, raf = 0, last = performance.now(), clock = 0, lit = 0;
        const io = window.IntersectionObserver ? new window.IntersectionObserver(([e]) => { onScreen = e.isIntersecting; }) : null;
        if (io) io.observe(frame);
        const tick = (now) => {
            const dt = Math.min(64, now - last);
            last = now;
            if (onScreen && !document.hidden) clock += dt;
            const end = LAND_MS + glide + END_HOLD_MS;
            if (clock >= end + LIFT_MS) {
                clock = 0;
                lit = 0;
                marks.forEach(m => m.classList.remove('is-lit'));
                setCount(0);
            }
            if (clock < end) {
                const e = easeOutBack(Math.min(1, clock / ENTER_MS));
                sheet.style.opacity = String(Math.min(1, clock / 260));
                sheet.style.transform = `translateY(${46 * (1 - e)}px) rotate(${-1.1 * (1 - e)}deg) scale(${0.97 + 0.03 * e})`;
            } else {
                const u = (clock - end) / LIFT_MS;
                sheet.style.opacity = String(1 - u);
                sheet.style.transform = `translateY(${-28 * u}px) scale(${1 - 0.02 * u})`;
            }
            const k = Math.min(1, Math.max(0, (clock - LAND_MS) / glide));
            const y = travel * easeInOutSine(k);
            camera.style.transform = `translate3d(0, ${-y}px, 0)`;
            // Nothing lights until the sheet has landed; then cites light as they reach the reading line.
            const line = clock < LAND_MS ? -1 : y + visible * 0.74;
            let n = 0;
            marks.forEach(m => {
                const on = m.offsetTop < line;
                if (on && !m.classList.contains('is-lit')) m.classList.add('is-lit');
                if (on && m.classList.contains('film-cite')) n += 1;
            });
            if (n !== lit) { lit = n; setCount(n); }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => {
            cancelAnimationFrame(raf);
            if (io) io.disconnect();
        };
    }, [scale, reduce, citeTotal, blocks, top]);

    return (
        <div ref={frameRef} className={`film-page-frame ${desk ? 'film-desk' : ''} ${className}`.trim()}>
            <div className="film-page-viewport" style={{ WebkitMaskImage: viewportMask(top), maskImage: viewportMask(top) }}>
                <div className="film-page-rig" style={{ top, width: PAGE_W, transform: `translateX(-50%) scale(${scale})`, opacity: scale ? 1 : 0 }}>
                    <div className="film-page-camera" ref={cameraRef}>
                        <div className="film-page-sheet" ref={sheetRef} style={reduce ? undefined : { opacity: 0 }}>
                            <div className="film-page" ref={pageRef}>
                                {blocks.map((b, i) => {
                                    const cap = b.caption ? ' film-page-caption' : '';
                                    if (b.type === 'rule') return <hr key={i} className="film-page-rule" />;
                                    if (b.type === 'title') return <div key={i} className={`film-page-title${cap}`}>{b.text}</div>;
                                    if (b.type === 'heading') return <div key={i} className={`film-page-heading${cap}`}>{withMarks(b.text)}</div>;
                                    return (
                                        <p key={i} className={`film-page-${b.type}${cap}`}>
                                            {b.lead && <span className="film-page-lead">{withMarks(b.lead)} </span>}
                                            {withMarks(b.text)}
                                        </p>
                                    );
                                })}
                            </div>
                            <div className="film-page-folio" aria-hidden="true">1</div>
                        </div>
                    </div>
                </div>
            </div>
            {citeTotal ? (
                <div className="film-page-chip" aria-hidden="true">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5" /><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5" /></svg>
                    Citations linked <span ref={countRef}>0/{citeTotal}</span>
                </div>
            ) : null}
        </div>
    );
}

// One-shot reveal state for CSS-driven entrances: 'idle' (static, nothing hidden: no JS, no observer, or
// reduced motion), 'armed' (start pose applied) and 'shown' (entered the viewport; play the entrance).
export function useFilmReveal(ref, rootMargin = '0px 0px -12% 0px') {
    const reduce = useReducedMotion();
    const [state, setState] = useState('idle');
    useEffect(() => {
        const el = ref.current;
        if (!el || reduce || !window.IntersectionObserver) return undefined;
        setState('armed');
        const io = new window.IntersectionObserver(([e]) => {
            if (e.isIntersecting) {
                setState('shown');
                io.disconnect();
            }
        }, { rootMargin });
        io.observe(el);
        return () => io.disconnect();
    }, [ref, reduce, rootMargin]);
    return state;
}

// The films leave a ring where the cursor clicks. Remounts (and replays) each time `at` changes.
export function ClickRipple({ at, className = '' }) {
    if (!at) return null;
    return <span key={at} className={`film-ripple ${className}`.trim()} aria-hidden="true" />;
}
